import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  InputAdornment,
  TextField,
  Stack,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import DataTable from '../../components/tables/DataTable';
import { getItems } from '../../data/items';

const InventoryList = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selected, setSelected] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortColumn, setSortColumn] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');

  const inventory = useMemo(() => getItems(), []);

  const filteredInventory = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return inventory;

    return inventory.filter((item) => {
      const haystack = `${item.name} ${item.sku} ${item.purchaseDescription}`.toLowerCase();
      return haystack.includes(query);
    });
  }, [inventory, searchQuery]);

  const columns = [
    {
      id: 'name',
      label: 'Item',
      sortable: true,
      render: (value, row) => (
        <Box>
          <Typography sx={{ fontWeight: 600 }}>{value}</Typography>
          <Typography variant="caption" color="text.secondary">{row.sku}</Typography>
        </Box>
      ),
    },
    { id: 'purchaseDescription', label: 'Purchase Description', sortable: true },
    {
      id: 'costPrice',
      label: 'Cost',
      sortable: true,
      render: (value) => `${Number(value).toLocaleString()}`,
    },
    { id: 'description', label: 'Description', sortable: true },
    {
      id: 'sellingPrice',
      label: 'Rate',
      sortable: true,
      render: (value) => `${Number(value).toLocaleString()}`,
    },
    { id: 'unit', label: 'Unit', sortable: true },
  ];

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      setSelected(inventory.map((item) => item.id));
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, row) => {
    navigate(`/dashboard/items/${row.id}`);
  };

  const handleSort = (columnId, direction) => {
    setSortColumn(columnId);
    setSortDirection(direction);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Items
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage items, stock, and purchase details in a Zoho-style workflow.
        </Typography>
      </Stack>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="body2" color="text.secondary">Total Items</Typography>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>{inventory.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="body2" color="text.secondary">Total Stock</Typography>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>{inventory.reduce((sum, item) => sum + item.stock, 0)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="body2" color="text.secondary">Low Stock</Typography>
              <Typography variant="h4" sx={{ fontWeight: 600, color: 'warning.main' }}>
                {inventory.filter((item) => item.stock < 20).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="body2" color="text.secondary">Inventory Value</Typography>
              <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
                {inventory.reduce((sum, item) => sum + item.costPrice * item.stock, 0).toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search items..."
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/dashboard/items/new')}
                >
                  New Item
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <DataTable
          columns={columns}
          data={filteredInventory}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={setPage}
          onRowsPerPageChange={setRowsPerPage}
          selectable
          selected={selected}
          onSelectAllClick={handleSelectAllClick}
          onClick={handleClick}
          onSort={handleSort}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
        />
      </Card>
    </Box>
  );
};

export default InventoryList;
